import React, { useState } from "react";
import {
  FormControl,
  FormLabel,
  Input,
  Button,
  Box,
  Text,
  useToast,
  Select,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { useForm, Controller } from "react-hook-form";
import { useAddFamilyMemberStore } from "../store";
import { servicebookingFlowStore } from "../store/ServiceBookingFlowStore";
import { useAddFamilyMemberToUserMutation } from "../graphql/types-and-hooks";
import dayjs from "dayjs";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { observer } from 'mobx-react-lite'; 
import * as PatientService from "../services/PatientService"; 

interface CreateFamilyMemberStepProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AddFamilyMemberPersonalDetailsData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  mobileNumber: string;
}

// Wrap the component with observer
const CreateFamilyMemberStep: React.FC<CreateFamilyMemberStepProps> = observer(({ onClose }) => {
  const [showMfaInput, setShowMfaInput] = useState(false);
  const toast = useToast();

  const [personalDetails, setData] = useAddFamilyMemberStore(
    (state) => [state.personalDetails, state.setData, state.resetData]
  );

  // Now this is safe because the component is observed
  const isServiceBooking = servicebookingFlowStore.isServiceBooking;

  const { control, handleSubmit, reset, formState: { errors } } = useForm<AddFamilyMemberPersonalDetailsData>({
    defaultValues: {
      firstName: personalDetails?.firstName || "",
      lastName: personalDetails?.lastName || "",
      dateOfBirth: personalDetails?.dateOfBirth || null,
      gender: personalDetails?.gender || "",
      mobileNumber: personalDetails?.mobileNumber || "",
    },
  });

  const [
    addFamilyMemberToUserMutation,
    { loading: addFamilyMemberToUserLoading },
  ] = useAddFamilyMemberToUserMutation({
    refetchQueries: ["User", "UpcomingAppointments"],
    awaitRefetchQueries: true,
  });

  const [mfaCode, setMfaCode] = useState("");

  const onSubmit = handleSubmit(async (data: AddFamilyMemberPersonalDetailsData) => {
    const { firstName, lastName, dateOfBirth, gender, mobileNumber } = data;

    const formattedData = {
      ...data,
      dateOfBirth: data.dateOfBirth ? dayjs(data.dateOfBirth).format('DD/MM/YYYY') : null
    };

    // Basic validation
    if (!firstName || !lastName || !dateOfBirth || !gender || !mobileNumber) {
      toast({
        title: "Error",
        description: "All fields are required",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setData({
        personalDetails: data,
      });

      await PatientService.sendFamilyMemberVerification(data.mobileNumber);
      

      setData({
        personalDetails: formattedData,
      });

      setShowMfaInput(true); // Open the MFA modal
    } catch {
      toast({
        title: "Error",
        description: "Failed to create family member",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  });

  const handleMfaSubmit = async () => {
    try {
      await addFamilyMemberToUserMutation({
        variables: {
          input: {
            first_name: personalDetails.firstName,
            last_name: personalDetails.lastName,
            dob: personalDetails.dateOfBirth,
            gender: personalDetails.gender,
            phone_number: personalDetails.mobileNumber,
            code: mfaCode,
          },
        },
      });

      toast({
        title: "Success",
        description: "MFA code verified successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      reset(); // Reset the form after successful submission
      onClose();

    } catch (error) {
      if (
        !["VERIFICATION_CODE_MISMATCH", "VERIFICATION_CODE_EXPIRED"]?.includes(
          error?.graphQLErrors?.[0]?.extensions?.code
        )
      ) {
        console.log(error);
      }

      toast({
        title: "Error",
        description: "Invalid MFA code",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box>
      {!showMfaInput ? (
        // Family Member Details Form
        <form onSubmit={onSubmit}>
          <Text fontSize="md">Input the following details to proceed</Text>

          <FormControl mb={4} mt={4} isInvalid={!!errors.firstName}>
            <FormLabel>First Name</FormLabel>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: "First name is required" }}
              render={({ field }) => <Input {...field} placeholder="Enter First Name" />}
            />
          </FormControl>

          <FormControl mb={4} isInvalid={!!errors.lastName}>
            <FormLabel>Last Name</FormLabel>
            <Controller
              name="lastName"
              control={control}
              rules={{ required: "Last name is required" }}
              render={({ field }) => <Input {...field} placeholder="Enter Last Name" />}
            />
          </FormControl>

          <FormControl mb={4} isInvalid={!!errors.dateOfBirth}>
            <FormLabel>Date of Birth</FormLabel>
            <Controller
              name="dateOfBirth"
              control={control}
              rules={{ required: "Date of birth is required" }}
              render={({ field }) => (
                <Input {...field} type="date" placeholder="Select Date" />
              )}
            />
          </FormControl>

          <FormControl mb={4} isInvalid={!!errors.gender}>
            <FormLabel>Gender</FormLabel>
            <Controller
              name="gender"
              control={control}
              rules={{ required: "Gender is required" }}
              render={({ field }) => (
                <Select {...field} placeholder="Select Gender">
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </Select>
              )}
            />
          </FormControl>

          <FormControl mb={4} isInvalid={!!errors.mobileNumber}>
            <FormLabel>Mobile Number</FormLabel>
            <Controller
              name="mobileNumber"
              control={control}
              rules={{
                required: "Mobile number is required",
                validate: (value) => {
                  const phoneNumber = parsePhoneNumberFromString(value, "AU");
                  return phoneNumber && phoneNumber.isValid()
                    ? true
                    : "Invalid phone number format";
                },
              }}
              render={({ field: { onChange, value, ...rest } }) => (
                <InputGroup>
                  <Input
                    {...rest}
                    value={value}
                    placeholder="Enter Phone Number"
                    onBlur={(e) => {
                      const input = e.target.value;
                      const phoneNumber = parsePhoneNumberFromString(input, "AU");
                      if (phoneNumber && phoneNumber.isValid()) {
                        onChange(phoneNumber.formatInternational());
                      } else {
                        onChange(input);
                      }
                    }}
                    onChange={onChange}
                  />
                  <InputRightElement>
                    <span role="img" aria-label="Australian Flag" style={{ fontSize: "1.5rem" }}>
                      🇦🇺
                    </span>
                  </InputRightElement>
                </InputGroup>
              )}
            />
          </FormControl>

          <Button
            type="submit"
            bg={isServiceBooking ? servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg : "#B92F2F"}
            color="#fff"
            mt={5}
            width="100%"
          >
            Create an Account
          </Button>
        </form>
      ) : (
        // MFA Code Input
        <Box>
          <Text fontSize="md" mb={4}>
            Enter the verification code sent to your phone.
          </Text>
          <FormControl mb={4}>
            <FormLabel>MFA Code</FormLabel>
            <Input
              placeholder="Enter MFA Code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
            />
          </FormControl>
          <Button
            onClick={handleMfaSubmit}
            bg={isServiceBooking ? servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg : "#B92F2F"}
            color="#fff"
            mt={5}
            width="100%"
            isLoading={addFamilyMemberToUserLoading}
          >
            Verify MFA Code
          </Button>
        </Box>
      )}
    </Box>
  );
});

export default CreateFamilyMemberStep;